// Prevent multiple initializations
if (!window.ProductLoadMore) {
  window.ProductLoadMore = class {
    constructor() {
      // Store instance state
      this.state = {
        isLoading: false,
        hasMoreProducts: true,
        currentPage: 1,
        loadedProducts: 0,
        totalResults: 0,
      };

      // Initialize core elements 
      this.productGrid = document.getElementById("product-grid");
      const gridContainer = this.productGrid?.closest("#ProductGridContainer") || document;
      this.loadMoreButton = gridContainer.querySelector('[data-load-more="search"]') || gridContainer.querySelector(".load-more_btn");
      this.buttonText = this.loadMoreButton?.querySelector(".button-text");
      this.loadingSpinner =
        this.loadMoreButton?.querySelector(".loading__spinner");

      // Bind methods
      this.handleLoadMore = this.handleLoadMore.bind(this);

      // Inject skeleton styles
      this.injectSkeletonStyles();

      // Initialize if all required elements are present
      if (this.validateElements()) {
        this.initialize();
      }
    }

    injectSkeletonStyles() {
      if (document.getElementById("load-more-skeleton-styles")) return;

      const styles = document.createElement("style");
      styles.id = "load-more-skeleton-styles";
      styles.textContent = `
        .skeleton-card {
          opacity: 1;
        }
        
        .skeleton-card__inner {
          background: var(--color-background, #fff);
          border-radius: 4px;
          overflow: hidden;
        }
        
        .skeleton-card__image {
          width: 100%;
          padding-bottom: 125%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          border-radius: 4px;
        }
        
        .skeleton-card__content {
          padding: 12px 0;
        }
        
        .skeleton-card__title {
          height: 16px;
          width: 80%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          border-radius: 4px;
          margin-bottom: 8px;
        }
        
        .skeleton-card__price {
          height: 14px;
          width: 40%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          border-radius: 4px;
        }
        
        .skeleton-animation {
          animation: skeleton-shimmer 1.5s infinite;
        }
        
        @keyframes skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        
        .product-card-fade-in {
          animation: productFadeIn 0.3s ease-out forwards;
        }
        
        @keyframes productFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `;
      document.head.appendChild(styles);
    }

    showSkeletonCards(count) {
      if (!this.productGrid) return;

      for (let i = 0; i < count; i++) {
        const skeleton = document.createElement("li");
        skeleton.className = "grid__item skeleton-card";
        skeleton.innerHTML = `
          <div class="skeleton-card__inner">
            <div class="skeleton-card__image skeleton-animation"></div>
            <div class="skeleton-card__content">
              <div class="skeleton-card__title skeleton-animation"></div>
              <div class="skeleton-card__price skeleton-animation"></div>
            </div>
          </div>
        `;
        this.productGrid.appendChild(skeleton);
      }
    }

    removeSkeletonCards() {
      if (!this.productGrid) return;

      const skeletons = this.productGrid.querySelectorAll(".skeleton-card");
      skeletons.forEach((skeleton) => skeleton.remove());
    }

    countLoadedProducts() {
      if (!this.productGrid) return 0;
      return this.productGrid.querySelectorAll(
        ".grid__item:not(.grid__item--cta)"
      ).length;
    }

    validateElements() {
      if (
        !this.productGrid ||
        !this.loadMoreButton ||
        !this.buttonText
      ) {
        // Silently return if load more elements aren't present on this page
        return false;
      }
      return true;
    }

    initialize() {
      // Remove any existing listeners and add new one
      this.loadMoreButton.removeEventListener("click", this.handleLoadMore);
      this.loadMoreButton.addEventListener("click", this.handleLoadMore);

      // Set initial state based on data attributes
      const nextUrlElement = document.getElementById("paginateNext");
      if (nextUrlElement) {
        this.nextUrlElement = nextUrlElement;
        this.productsPerPage =
          parseInt(nextUrlElement.dataset.productsPerPage, 10) || 24;
        this.state.totalResults =
          parseInt(nextUrlElement.dataset.totalResults, 10) || 0;
        this.syncLoadedProductState(nextUrlElement);
      } else {
        this.nextUrlElement = null;
      }

      this.updateButtonVisibility();
    }

    syncLoadedProductState(nextUrlElement) {
      this.state.loadedProducts = this.countLoadedProducts();
      this.state.hasMoreProducts = !!nextUrlElement.dataset.nextUrl?.trim();

      if (
        !this.state.hasMoreProducts &&
        this.state.totalResults > 0 &&
        this.state.loadedProducts < this.state.totalResults
      ) {
        this.state.hasMoreProducts = true;
      }
    }

    updateButtonVisibility() {
      this.state.loadedProducts = this.countLoadedProducts();

      const hasRemainingProducts =
        this.state.totalResults > 0 &&
        this.state.loadedProducts < this.state.totalResults;
      const shouldHide = !this.state.hasMoreProducts && !hasRemainingProducts;

      this.loadMoreButton.style.display = shouldHide ? "none" : "block";
    }

    setLoading(isLoading) {
      this.state.isLoading = isLoading;
      if (isLoading) {
        this.loadMoreButton.setAttribute("data-loading", "");
      } else {
        this.loadMoreButton.removeAttribute("data-loading");
      }
      if (this.loadingSpinner) {
        this.loadingSpinner.classList.toggle("hidden", !isLoading);
      }
    }

    async getNextPageUrl() {
      const rawNext = this.nextUrlElement?.dataset?.nextUrl;
      if (rawNext && rawNext.trim()) {
        try {
          const base = window.location.origin + window.location.pathname;
          const resolved = new URL(rawNext.trim(), base);
          this.mergeActiveFilterParams(resolved);
          return resolved.toString();
        } catch (e) {
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.set("page", (this.state.currentPage + 1).toString());
      if (!url.searchParams.has("page_size")) {
        url.searchParams.set("page_size", this.productsPerPage);
      }
      return url.toString();
    }

    mergeActiveFilterParams(targetUrl) {
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (key === "page" || key === "page_size" || key === "section_id") {
          return;
        }
        if (!targetUrl.searchParams.has(key)) {
          targetUrl.searchParams.append(key, value);
        }
      });
    }

    async fetchNextPage() {
      try {
        const url = await this.getNextPageUrl();
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Stream the response instead of loading it all at once
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let html = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          html += decoder.decode(value, { stream: true });
        }

        return html;
      } catch (error) {
        console.error("Error fetching next page:", error);
        throw error;
      }
    }

    appendNewProducts(productElements) {
      // Add a safety limit for number of products to append at once
      const MAX_PRODUCTS_PER_BATCH = this.productsPerPage || 36;
      const fragment = document.createDocumentFragment();

      // Convert to array and process in smaller batches if needed
      const products = Array.from(productElements).filter(
        (product) => !product.classList.contains("grid__item--cta")
      );
      const productsToAdd = products.slice(0, MAX_PRODUCTS_PER_BATCH);

      productsToAdd.forEach((product, index) => {
        try {
          const clone = product.cloneNode(true);
          // Add fade-in animation with stagger
          clone.classList.add("product-card-fade-in");
          clone.style.animationDelay = `${index * 50}ms`;
          fragment.appendChild(clone);
        } catch (error) {
          console.error("Error cloning product:", error);
          // Continue with other products if one fails
        }
      });

      try {
        this.productGrid.appendChild(fragment);
        this.state.loadedProducts = this.countLoadedProducts();

        if (products.length > MAX_PRODUCTS_PER_BATCH) {
          console.warn(
            `Product batch size limited to ${MAX_PRODUCTS_PER_BATCH}. Remaining products will be loaded in next batch.`
          );
        }
      } catch (error) {
        console.error("Error appending products:", error);
        this.state.hasMoreProducts = false;
      }
    }

    async handleLoadMore() {
      try {
        if (
          window.MarketPriceSort &&
          window.MarketPriceSort.getState &&
          typeof window.MarketPriceSort.getState === "function" &&
          window.MarketPriceSort.getState().isMarketPriceSortActive
        ) {
          return;
        }
      } catch (e) {
      }

      if (this.state.isLoading || !this.state.hasMoreProducts) return;

      try {
        this.setLoading(true);

        // Show skeleton cards while loading
        const expectedProducts = Math.min(
          this.productsPerPage,
          this.state.totalResults - this.state.loadedProducts
        );
        this.showSkeletonCards(expectedProducts);

        const nextPageHtml = await this.fetchNextPage();
        const parser = new DOMParser();
        const nextPageDoc = parser.parseFromString(nextPageHtml, "text/html");

        const newProductGrid = nextPageDoc.getElementById("product-grid");
        if (!newProductGrid) {
          this.removeSkeletonCards();
          throw new Error("Could not find product grid in response");
        }

        const newProducts = newProductGrid.querySelectorAll(
          ".grid__item:not(.grid__item--cta)"
        );
        if (newProducts.length === 0) {
          this.removeSkeletonCards();
          throw new Error("No products found in response");
        }

        // Remove skeletons and add real products
        this.removeSkeletonCards();
        this.appendNewProducts(newProducts);
        this.state.currentPage++;

        const nextUrlElement = nextPageDoc.getElementById("paginateNext");
        if (this.nextUrlElement && nextUrlElement) {
          this.nextUrlElement.dataset.nextUrl =
            nextUrlElement.dataset.nextUrl || "";
          this.nextUrlElement.dataset.totalResults =
            nextUrlElement.dataset.totalResults ||
            this.nextUrlElement.dataset.totalResults;
          this.state.totalResults =
            parseInt(this.nextUrlElement.dataset.totalResults, 10) ||
            this.state.totalResults;
          this.syncLoadedProductState(this.nextUrlElement);
        } else {
          this.state.hasMoreProducts = false;
        }

        this.updateButtonVisibility();

        // Dispatch the event after a small delay to ensure DOM is fully updated
        setTimeout(() => {
          document.dispatchEvent(
            new CustomEvent("products:added", {
              detail: {
                productsAdded: newProducts.length,
              },
            })
          );
        }, 50);
      } catch (error) {
        console.error("Error loading more products:", error);
        this.removeSkeletonCards();
        this.state.hasMoreProducts = false;
        this.updateButtonVisibility();
      } finally {
        this.setLoading(false);
      }
    }

    destroy() {
      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener("click", this.handleLoadMore);
      }
    }
  };

  // Initialize load more functionality
  let loadMoreInstance = null;

  function initializeLoadMore() {
    if (loadMoreInstance) {
      loadMoreInstance.destroy();
    }
    loadMoreInstance = new ProductLoadMore();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeLoadMore);
  } else {
    initializeLoadMore();
  }

  document.addEventListener("products:loaded", () => {
    requestAnimationFrame(() => {
      if (loadMoreInstance) {
        loadMoreInstance.destroy();
      }
      loadMoreInstance = new ProductLoadMore();
    });
  });

  document.addEventListener("products:added", () => {
    requestAnimationFrame(() => {
      if (!loadMoreInstance) return;
      loadMoreInstance.updateButtonVisibility();
    });
  });
}
